import { JetView } from 'webix-jet';
import { devConfig, profile } from 'config/config';

/**
 * Class representing the FilterApprovalQuotaRetractionView.
 * Extends JetView to create a custom view for filtering approval quota retraction.
 */
export default class FilterApprovalQuotaRetractionView extends JetView {
    /**
     * Configures the layout of the view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        const _ = this.app.getService('locale')._; // Localization service
        const screen = this.app.config.size; // Screen size configuration
        const theme = this.app.config.theme; // Theme configuration

        // Left section of the form with input fields
        const left = {
            margin: 5,
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    cols: [
                        {
                            view: 'text', // Text input for datepicker
                            name: 'Datepicker',
                            id: 'Datepicker',
                            css: 'reportrange',
                            readonly: true,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Label for the datepicker
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                        },
                        {
                            view: 'datepicker', // Datepicker input
                            name: 'Date',
                            id: 'Date',
                            stringResult: true,
                            minWidth: 200,
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha'), // Label for the date input
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: moment().format('YYYY-MM-DD'), // Default value
                            format: '%d %M %Y', // Date format
                            suggest: {
                                type: 'calendar',
                                body: {
                                    maxDate: new Date(), // Maximum selectable date
                                },
                            },
                        },
                        {
                            view: 'text', // Text input for ID
                            name: 'Id',
                            type: 'number',
                            label: "<span class='icon-drivers-license-o'></span>" + _('Id'), // Label for ID input
                            labelPosition: 'top',
                            placeholder: _('ID'), // Placeholder text
                        },
                        {
                            view: 'text', // Text input for user who created
                            name: 'UserCreate',
                            label: "<span class='icon-mail'></span>" + _('Usuario Creó'), // Label for user input
                            labelPosition: 'top',
                            placeholder: _('Usuario Creó'), // Placeholder text
                        },
                    ],
                },
            ],
        };

        // Second left section of the form with additional input fields
        const left2 = {
            margin: 5,
            rows: [
                {
                    margin: 15,
                    css: 'on-row',
                    cols: [
                        {
                            view: 'richselect', // Dropdown for state selection
                            name: 'State',
                            label: _('Estado'), // Label for state selection
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar'), // Placeholder text
                            options: [
                                { id: '', value: _('Pendiente') }, // Pending option
                                { id: '0', value: _('Aprobada') }, // Approved option
                                { id: '1', value: _('Rechazada') }, // Rejected option
                            ],
                        },
                        {
                            view: 'text', // Text input for minimum value
                            name: 'ValueMinimum',
                            type: 'number',
                            label: "<span class='icon-cash'></span>" + _('Valor Mínimo'), // Label for minimum value input
                            labelPosition: 'top',
                            placeholder: _('Valor Mínimo'), // Placeholder text
                        },
                        {
                            view: 'text', // Text input for maximum value
                            name: 'ValueMaximum',
                            type: 'number',
                            label: "<span class='icon-cash'></span>" + _('Valor Máximo'), // Label for maximum value input
                            labelPosition: 'top',
                            placeholder: _('Valor Máximo'), // Placeholder text
                        },
                    ],
                },
            ],
        };

        // Upper section of the form containing the first left section
        const upper_section = {
            css: 'father-container',
            height: document.body.offsetWidth < 500 ? 190 : 0, // Responsive height
            rows: [
                {
                    margin: 10,
                    cols: [left], // Contains the first left section
                },
            ],
        };

        // Upper section 2 of the form containing the second left section
        const upper_section2 = {
            css: 'father-container',
            height: document.body.offsetWidth < 500 ? 190 : 0, // Responsive height
            rows: [
                {
                    margin: 10,
                    cols: [left2], // Contains the second left section
                },
            ],
        };

        // Accordion section for advanced filters
        const accordion_section = {
            id: 'b5',
            rows: [
                {
                    multi: true,
                    view: 'accordion',
                    css: 'betShop',
                    collapsed: true, // Initially collapsed
                    paddingY: 10,
                    type: 'clean',
                    rows: [
                        {
                            header: _('Filtros Avanzados'), // Header for advanced filters
                            body: {
                                padding: 10,
                                rows: [upper_section2], // Contains the second upper section
                            },
                        },
                    ],
                },
            ],
        };

        // Button section for actions
        const buttons = {
            margin: 5,
            cols: [
                {},
                {
                    view: 'button', // Clear button
                    value: _('Limpiar'), // Button label
                    autowidth: true,
                    click: () => {
                        this.getRoot().clear(); // Clears the form
                    },
                },
                {
                    view: 'button', // Consult button
                    value: _('Consultar'), // Button label
                    hotkey: 'enter', // Shortcut key
                    type: 'form',
                    autowidth: true,
                    click: () => {
                        const filterdata = this.getRoot().getValues(); // Get form values
                        if (profile.typeDate !== 2) {
                            delete filterdata['Date']; // Remove Date if typeDate is not 2
                        } else {
                            if (true) {
                                delete filterdata['Datepicker']; // Remove Datepicker if typeDate is 2
                            }
                        }
                        this.app.callEvent('requests:filter', [filterdata]); // Trigger filter event
                    },
                },
            ],
        };

        // Return the complete form configuration
        return {
            view: 'form',
            id: 'formFilter',
            rows: [upper_section, accordion_section, buttons], // Form layout
            rules: {
                fname: webix.rules.isNotEmpty, // Validation rule
            },
        };
    }

    /**
     * Initializes the view and sets up event listeners.
     * @param {Object} grid - The grid object to initialize.
     */
    init(grid) {
        webix.ready(function () {
            if (profile.typeDate !== 2) {
                $$('Date').hide(); // Hide Date input if typeDate is not 2
                $$('Datepicker').show(); // Show Datepicker
            } else {
                if (true) {
                    $$('Date').show(); // Show Date input if typeDate is 2
                    $$('Datepicker').hide(); // Hide Datepicker
                }
            }
        });
        const lang = this.app.getService('locale').getLang(); // Get current language
        $$('Datepicker').attachEvent('onAfterRender', function () {
            devConfig.setDateModal(lang); // Set date modal based on language
        });
    }
}

// FILE DOCUMENTED
